// CohortChallengeBanner — Overview tab card showing the active cohort
// challenge (admin-defined) with countdown to end + mini leaderboard.
// Hidden when no challenge is currently within its date window.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { THEME } from "./state";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtCountdown(ms) {
  if (ms <= 0) return "0d 0h";
  const d = Math.floor(ms / 86_400_000);
  const h = Math.floor((ms % 86_400_000) / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function CohortChallengeBanner() {
  const [data, setData] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const token = localStorage.getItem("flexx_access_token");
        if (!token) return;
        const res = await axios.get(`${API}/cohort-challenges/active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (mounted) setData(res.data);
      } catch (_) { /* silent */ }
    };
    load();
    const i = setInterval(load, 5 * 60_000); // refresh every 5 min
    return () => { mounted = false; clearInterval(i); };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  if (!data?.challenge) return null;

  const c = data.challenge;
  const endMs = (() => {
    try { return new Date(c.end_date).getTime(); } catch (_) { return 0; }
  })();
  const remaining = Math.max(0, endMs - now);
  const top = (data.leaderboard || []).slice(0, 5);

  return (
    <div data-testid="cohort-challenge-banner" className="flexx-card" style={{
      borderLeft: `3px solid ${THEME.gold}`,
      background: `linear-gradient(135deg, #0c0c0c 0%, #131008 100%)`,
      boxShadow: `0 0 28px rgba(201,162,39,0.12)`,
      marginTop: 16,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <div>
          <div style={{ fontFamily: "monospace", color: THEME.gold, fontSize: 10, letterSpacing: 2 }}>🎯 ACTIVE COHORT CHALLENGE</div>
          <div data-testid="cohort-challenge-title" style={{ color: THEME.text, fontSize: 20, fontWeight: 800, marginTop: 4 }}>
            {c.title}
          </div>
        </div>
        <div data-testid="cohort-challenge-countdown" style={{
          padding: "4px 12px", borderRadius: 999,
          background: `${THEME.gold}15`, border: `1px solid ${THEME.gold}`,
          color: THEME.gold, fontFamily: "monospace", fontSize: 11, letterSpacing: 1,
        }}>
          ENDS IN {fmtCountdown(remaining)}
        </div>
      </div>

      {c.description && (
        <div style={{ color: THEME.mute, fontSize: 13, lineHeight: 1.55, marginBottom: 12 }}>
          {c.description}
        </div>
      )}

      {c.prize_text && (
        <div data-testid="cohort-challenge-prize" style={{
          padding: 10, borderRadius: 8,
          background: `${THEME.gold}10`, border: `1px solid ${THEME.gold}55`,
          color: THEME.text, fontSize: 13, marginBottom: 12,
        }}>
          🏆 <b style={{ color: THEME.gold }}>Prize:</b> {c.prize_text}
        </div>
      )}

      {top.length > 0 && (
        <div data-testid="cohort-challenge-leaderboard">
          <div style={{ fontFamily: "monospace", color: THEME.mute, fontSize: 9, letterSpacing: 1.5, marginBottom: 6 }}>TOP 5</div>
          <div style={{ display: "grid", gap: 4 }}>
            {top.map((r) => (
              <div key={r.rank} data-testid={`cohort-rank-${r.rank}`}
                style={{
                  display: "grid", gridTemplateColumns: "28px 1fr auto", gap: 8,
                  padding: "6px 10px", borderRadius: 6,
                  background: r.rank <= 3 ? `${THEME.gold}10` : "transparent",
                  alignItems: "center",
                }}>
                <span style={{ color: r.rank === 1 ? "#FFD700" : THEME.mute, fontFamily: "monospace", fontSize: 11, fontWeight: 700 }}>
                  {r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : `#${r.rank}`}
                </span>
                <span style={{ color: THEME.text, fontSize: 12 }}>{r.name}</span>
                <span style={{ color: THEME.gold, fontFamily: "monospace", fontSize: 11 }}>{r.xp.toLocaleString()} XP</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 10, color: THEME.mute, fontFamily: "monospace", fontSize: 10, letterSpacing: 0.5, fontStyle: "italic" }}>
        Ranked by total XP. Earn the most XP during the challenge window to climb.
      </div>
    </div>
  );
}
